import { useMemo } from 'preact/hooks'
import config from '../Config.js'
import { useLocale } from '../contexts/index.js'
import type { VersionId } from '../services/index.js'
import { MIRRORED_VERSION } from '../services/Versions.js'
import { Store } from '../Store.js'
import { Btn } from './Btn.js'
import { BtnMenu } from './BtnMenu.js'

interface Props {
	value?: VersionId,
	allowed?: VersionId[],
	hasAny?: boolean,
	onChange: (version: VersionId) => void,
	onAny?: () => void,
}
export function VersionSwitcher({ value, allowed, hasAny, onChange, onAny }: Props) {
	const { locale } = useLocale()

	// ULICRAFT FORK — only the MC version our server actually runs is offered.
	//
	// We mirror mcmeta for ONE version (1.21, whose mcmeta ref is 1.21.1). Every
	// other version in config.versions has no mirrored data, so selecting it
	// would produce empty registries and a permanent spinner — at the party,
	// with no internet and no way to tell why. Failing closed beats that.
	//
	// This filters the UI ONLY. config.versions and VersionIds keep all their
	// entries on purpose: checkVersion() compares them BY INDEX
	// (config.versions.findIndex), and a generator's minVersion/maxVersion is
	// resolved the same way. Deleting entries would make findIndex return -1 for
	// a removed bound, so `-1 <= version` passes and generators that do not
	// exist in 1.21 would silently render. Filter the view, never the data.
	const versions = useMemo(() => {
		if (allowed) {
			return allowed
				.map(a => config.versions.find(v => v.id === a)!)
				.filter(v => v !== undefined)
				.filter(v => v.id === MIRRORED_VERSION)
		}
		return config.versions.filter(v => v.id === MIRRORED_VERSION)
	}, [allowed])

	// No "More" escape hatch: there is nothing more to show, and offering it
	// would just be a route to the broken state described above.
	const shownVersions = versions

	return <BtnMenu class={`version-switcher${Store.getVersion() === null ? ' attention' : ''}`} icon="tag" label={value ?? locale('any_version')} tooltip={locale('switch_version')}>
		{hasAny && <Btn label={locale('any_version')} onClick={onAny} active={!value} />}
		{shownVersions.map(v =>
			<Btn label={v.name} active={v.id === value} onClick={() => onChange(v.id)} />)}
	</BtnMenu>
}
